import { ProductMapper } from "@omni/mappers/product/product.mapper";
import { ProductsMapper } from "@omni/mappers/product/products.mapper";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { AlgoliaRecommendParams } from "@omni/types/api/search/algolia/recommend/algoliaRecommendParams.model";
import { AlgoliaRecommendZone } from "@omni/types/api/search/algolia/shared/enum/algoliaRecommendZone.enum";
import { BffSearchProduct } from "@omni/types/api/search/bff/product/product.model";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { useBFFRecommendServiceApi } from "@/api/hooks/recommendService.hook";
import { ProductSliderComponent } from "@/components/Product/ProductSlider/productSlider.component";
import {
    CartItemModel,
    CartModel
} from "@/redux/modules/api/data/cart/@types/cart.model";
import { useProductCartContext } from "@/utils/hooks/cart.hook";

import { useProductSliderContext } from "../productSlider.context";
import { ProductSliderSkeletonComponent } from "../skeleton/productSliderSkeleton.component";

const getCartSkuList = (cart?: CartModel | null) => {
    const isRecommendationsAllowed = ({
        articleConfiguration,
        isAdditionalService,
        giftCardArticle
    }: CartItemModel) =>
        !articleConfiguration && !isAdditionalService && !giftCardArticle;

    return (
        cart?.items.filter(isRecommendationsAllowed).map((item) => item.sku) ??
        []
    );
};

type AlgoliaRecommendProductSliderProps = {
    algoliaRecommendParams: AlgoliaRecommendParams;
    sku?: string;
};

const AlgoliaRecommendProductslider = ({
    sku,
    algoliaRecommendParams
}: AlgoliaRecommendProductSliderProps) => {
    const { prefetchedProducts } = useProductSliderContext();
    const { cart } = useProductCartContext();
    const cartSkuList = getCartSkuList(cart);

    const [products, setProducts] = useState<
        Array<Product<ProductFieldSet.ProductTile>> | undefined
    >(prefetchedProducts);

    const [{ loading, error }, loadRecommendations] = useBFFRecommendServiceApi<
        any,
        Array<BffSearchProduct>,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/recommend/frequentlyBoughtTogether`,
            method: "POST",
            mapper: ProductsMapper.fromList(
                ProductMapper.fromBffSearchToProductTile
            )
        },
        {
            manual: true,
            useCache: false,
            autoCancel: false
        }
    );

    const updateRecommendations = useCallback(
        (skus: Array<string>) => {
            if (isEmpty(skus)) {
                setProducts([]);
            } else {
                loadRecommendations({
                    data: {
                        sku: skus.join(","),
                        algoliaRecommendClientConfig: {
                            indexName: algoliaRecommendParams.indexName,
                            threshold: algoliaRecommendParams.threshold,
                            maxRecommendations: algoliaRecommendParams.max
                        }
                    }
                }).then(({ data }) => {
                    setProducts(data ?? []);
                });
            }
        },
        [
            algoliaRecommendParams.indexName,
            algoliaRecommendParams.max,
            algoliaRecommendParams.threshold,
            loadRecommendations
        ]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isDefined(products)) return;
        if (loading || error) return;

        if (algoliaRecommendParams.zone === AlgoliaRecommendZone.ShoppingCart) {
            updateRecommendations(cartSkuList);
        } else if (sku) {
            updateRecommendations([sku]);
        }
    }, [cartSkuList, sku, products, setProducts, updateRecommendations]);

    if (error) {
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return <ProductSliderComponent products={products} />;
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};

export { AlgoliaRecommendProductslider };
